import React, { useMemo, useRef, useCallback } from 'react';
import type QuillType from 'quill';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { message } from 'antd';
import { uploadLiteratureNoteImage } from '../utils/uploadLiteratureNoteImage';
import { ensureQuillResizeRegistered } from './quillRegisterResize';
import { notesImagesToStubLinks, normalizeNotesHtmlToImgEmbed } from '../utils/literatureNotesImageStub';

ensureQuillResizeRegistered();

export interface RichTextEditorProps {
  /** 受控内容；可由 antd Form.Item 注入 */
  value?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  /** 编辑区高度（不含工具栏） */
  height?: number;
  /** compact：侧栏/窄区域；可与 linkStub 组合以精简工具栏 */
  variant?: 'default' | 'compact';
  /** 是否在工具栏显示插入图片（compact+linkStub 时也会在侧栏显示图片按钮） */
  showImage?: boolean;
  /**
   * inline：笔记内正常渲染图片（放大编辑等宽区）
   * linkStub：将 img 转为「📎 文件名」外链展示，便于窄侧栏插入与点击预览；存盘仍写回 img
   */
  imageDisplayMode?: 'inline' | 'linkStub';
  className?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange = () => {},
  placeholder = '请输入内容...',
  height = 200,
  variant = 'default',
  showImage = true,
  imageDisplayMode = 'inline',
  className
}) => {
  const quillRef = useRef<{ getEditor: () => QuillType } | null>(null);

  /** 父级存盘用 HTML 以 img 为准；含 Quill 剥掉 data-* 后仅剩「📎+图片链」的还原 */
  const displayValue = useMemo(() => {
    const canonical = normalizeNotesHtmlToImgEmbed(value);
    if (imageDisplayMode === 'linkStub') {
      return notesImagesToStubLinks(canonical);
    }
    return canonical;
  }, [value, imageDisplayMode]);

  const handleEditorChange = useCallback(
    (html: string) => {
      const next = normalizeNotesHtmlToImgEmbed(html);
      const prevCanonical = normalizeNotesHtmlToImgEmbed(value);
      if (next === prevCanonical) return;
      onChange(next);
    },
    [onChange, value]
  );

  const imageToolbarHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadLiteratureNoteImage(file);
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const range = editor.getSelection(true);
        const idx = range ? range.index : Math.max(0, editor.getLength() - 1);
        editor.insertEmbed(idx, 'image', url);
        editor.setSelection(idx + 1, 0);
      } catch (e: unknown) {
        message.error(e instanceof Error ? e.message : '图片上传失败');
      }
    };
  }, []);

  /** 点击图片后出现拖动手柄与对齐工具条；仅在有图片工具栏时启用 */
  const resizeModule = useMemo(
    () => ({
      modules: ['Resize', 'DisplaySize', 'Toolbar'],
      parchment: {
        image: {
          attribute: ['width'],
          limit: {
            minWidth: 40,
            maxWidth: 2000
          }
        }
      }
    }),
    []
  );

  const modules = useMemo(() => {
    const compactLinkStub = variant === 'compact' && imageDisplayMode === 'linkStub' && showImage;
    if (variant === 'compact') {
      if (compactLinkStub) {
        return {
          toolbar: {
            container: [
              [{ header: [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ color: [] }, { background: [] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link', 'image'],
              ['clean']
            ],
            handlers: {
              image: imageToolbarHandler
            }
          }
        };
      }
      return {
        toolbar: [
          [{ header: [1, 2, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          ['clean']
        ]
      };
    }
    if (!showImage) {
      return {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link'],
          ['clean']
        ]
      };
    }
    return {
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean']
        ],
        handlers: {
          image: imageToolbarHandler
        }
      },
      resize: resizeModule
    };
  }, [variant, showImage, imageDisplayMode, imageToolbarHandler, resizeModule]);

  /** Quill 2：列表只有 `list` 一种 format（值为 ordered/bullet），勿单独写 bullet */
  const formats = useMemo(() => {
    if (variant === 'compact') {
      if (showImage && imageDisplayMode === 'linkStub') {
        return ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'link', 'image'];
      }
      return ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'link'];
    }
    return ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'indent', 'align', 'link', 'image'];
  }, [variant, showImage, imageDisplayMode]);

  const chrome = variant === 'compact' ? height + 44 : height + 50;

  return (
    <div className={className} style={{ minHeight: chrome }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={displayValue}
        onChange={handleEditorChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{ height }}
      />
    </div>
  );
};

export default RichTextEditor;